import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Block, CustomBlockDefinition } from "./Editor";
import { TextBlock } from "./TextBlock";
import { ImageBlock } from "./ImageBlock";
import { TableBlock } from "./TableBlock";
import { DocumentBlock } from "./DocumentBlock";
import { Plus, GripVertical, Trash } from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface SortableBlockProps {
  block: Block;
  isActive: boolean;
  /** Whether the editor is editable. When false, content cannot be modified. Defaults to true. */
  editable?: boolean;
  onContentChange: (id: string, content: string) => void;
  onEnter: (id: string, contentBeforeCursor?: string, contentAfterCursor?: string) => void;
  onBackspace: (id: string, currentContent?: string) => void;
  onFocus: (id: string) => void;
  onArrowUp: (id: string) => void;
  onArrowDown: (id: string) => void;
  onCommand: (id: string, command: string) => void;
  onImageChange: (id: string, src: string, alt: string, size?: import('./ImageBlock').ImageSize, alignment?: import('./ImageBlock').ImageAlignment) => void;
  onTableChange: (id: string, data: import('./TableBlock').TableData) => void;
  onDelete: (id: string) => void;
  onBlockUpdate: (id: string, updates: Partial<Block>) => void;
  onAddBlock?: (id: string) => void;
  customBlocks: CustomBlockDefinition[];
  gameId?: string;
  // New props for document integration
  onDocumentContentUpdate?: (
    blockId: string,
    glossaryItemId: string,
    glossaryItemName: string
  ) => void;
  onDocumentSave?: () => void;
  onDocumentBlockCancel?: (blockId: string) => void;
  onRemoveBlock?: (blockId: string) => void;
  onMultilinePaste?: (id: string, lines: string[]) => void;
  /** Initial cursor position when the block becomes active */
  initialCursorPosition?: 'end' | number;
  /** Callback to clear the initial cursor position after it's been applied */
  onCursorPositionApplied?: () => void;
  /** Handler for when block metadata changes */
  onMetadataChange?: (id: string, metadata: Record<string, any>) => void;
  /** Handler for applying formatting across multiple blocks */
  onMultiBlockFormatting?: (command: string, value?: string) => void;
  /** Enable inline variable references ({{ key.name }} autocomplete and highlighting) */
  enableVariableReferences?: boolean;
  /** Available TypeItems for variable reference resolution */
  variableTypes?: import('@/lib/stores/typesStore').TypeItem[];
  /** Available ObjectItems for variable reference resolution */
  variableObjects?: import('@/lib/stores/objects/types').ObjectItem[];
}

export const SortableBlock = ({
  block,
  isActive,
  editable = true,
  onContentChange,
  onEnter,
  onBackspace,
  onFocus,
  onArrowUp,
  onArrowDown,
  onCommand,
  onImageChange,
  onTableChange,
  onDelete,
  onBlockUpdate,
  onAddBlock,
  gameId,
  customBlocks,
  onDocumentContentUpdate,
  onDocumentSave,
  onDocumentBlockCancel,
  onRemoveBlock,
  onMultilinePaste,
  initialCursorPosition,
  onCursorPositionApplied,
  onMetadataChange,
  onMultiBlockFormatting,
  enableVariableReferences = false,
  variableTypes = [],
  variableObjects = [],
}: SortableBlockProps) => {
  const { t } = usePlatformTranslation();
  const [showHandle, setShowHandle] = useState(false);
  const [longPressActive, setLongPressActive] = useState(false);
  const isMobile = useIsMobile();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id, disabled: !editable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const handleLongPressStart = () => {
    if (!isMobile) return;
    setTimeout(() => {
      setLongPressActive(true);
    }, 500);
  };

  const handleLongPressEnd = () => {
    setLongPressActive(false);
  };

  const handleAddBlock = () => {
    if (onAddBlock) {
      onAddBlock(block.id);
    }
  };

  const handleRemoveBlock = () => {
    onDelete(block.id);
  };

  const getPlaceholder = () => {
    switch (block.type) {
      case "heading":
        return "Heading";
      case "quote":
        return "Quote...";
      case "list":
        return "• List item";
      case "document":
        return "Click to create a document from blueprint";
      default:
        return "Type '/' for commands";
    }
  };

  const getClassName = () => {
    switch (block.type) {
      case "heading":
        return "text-2xl font-bold text-editor-text";
      case "quote":
        return "border-l-4 border-editor-handle pl-4 italic text-editor-text";
      case "list":
        return "text-editor-text";
      default:
        return "text-editor-text";
    }
  };

  // Check for custom block components
  const customBlock = customBlocks.find((cb) => cb.type === block.type);
  if (customBlock) {
    const CustomComponent = customBlock.component;
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="mb-1 group relative"
        onMouseEnter={() => editable && setShowHandle(true)}
        onMouseLeave={() => setShowHandle(false)}
        onTouchStart={editable ? handleLongPressStart : undefined}
        onTouchEnd={editable ? handleLongPressEnd : undefined}
      >
        {/* Handle Area - Controls visibility of + and drag handles (only when editable) */}
        {editable && (
          <div className="flex items-start gap-1 absolute left-0 top-0 -ml-20">
            {/* Remove Block Handle */}
            <button
              onClick={handleRemoveBlock}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
              title="Remove block"
            >
              <Trash size={16} />
            </button>
            {/* Add Block Handle */}
            <button
              onClick={handleAddBlock}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
              title="Add new block below"
            >
              <Plus size={16} />
            </button>

            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-grab active:cursor-grabbing transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
            >
              <GripVertical size={16} />
            </div>
          </div>
        )}

        <CustomComponent
          id={block.id}
          block={block}
          isActive={isActive}
          onUpdate={onBlockUpdate}
          onDelete={onDelete}
          onFocus={onFocus}
          {...customBlock.additionalProps}
        />
      </div>
    );
  }

  if (block.type === "image") {
    // Debug log to see what metadata is being passed
    console.log('SortableBlock rendering image block:', { 
      blockId: block.id, 
      metadata: block.metadata,
      hasSrc: !!block.metadata?.src 
    });
    
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="mb-1 group relative"
        onMouseEnter={() => editable && setShowHandle(true)}
        onMouseLeave={() => setShowHandle(false)}
        onTouchStart={editable ? handleLongPressStart : undefined}
        onTouchEnd={editable ? handleLongPressEnd : undefined}
      >
        {/* Handle Area - Controls visibility of + and drag handles (only when editable) */}
        {editable && (
          <div className="flex items-start gap-1 absolute left-0 top-0 -ml-20">
            {/* Remove Block Handle */}
            <button
              onClick={handleRemoveBlock}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
              title="Remove block"
            >
              <Trash size={16} />
            </button>
            {/* Add Block Handle */}
            <button
              onClick={handleAddBlock}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
              title="Add new block below"
            >
              <Plus size={16} />
            </button>

            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-grab active:cursor-grabbing transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
            >
              <GripVertical size={16} />
            </div>
          </div>
        )}

        <ImageBlock
          id={block.id}
          src={block.metadata?.src}
          alt={block.metadata?.alt}
          size={block.metadata?.size}
          alignment={block.metadata?.alignment}
          gameId={gameId}
          onImageChange={editable ? onImageChange : () => {}}
          onDelete={editable ? onDelete : () => {}}
        />
      </div>
    );
  }

  if (block.type === "table") {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="mb-1 group relative"
        onMouseEnter={() => editable && setShowHandle(true)}
        onMouseLeave={() => setShowHandle(false)}
        onTouchStart={editable ? handleLongPressStart : undefined}
        onTouchEnd={editable ? handleLongPressEnd : undefined}
      >
        {/* Handle Area - Controls visibility of + and drag handles (only when editable) */}
        {editable && (
          <div className="flex items-start gap-1 absolute left-0 top-0 -ml-20">
            {/* Remove Block Handle */}
            <button
              onClick={handleRemoveBlock}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
              title={t('platform.editor.sortableblock.remove_block_title_2_3')}
            >
              <Trash size={16} />
            </button>
            {/* Add Block Handle */}
            <button
              onClick={handleAddBlock}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
              title={t('platform.editor.sortableblock.add_new_block_below_title_2_3')}
            >
              <Plus size={16} />
            </button>

            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-grab active:cursor-grabbing transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
            >
              <GripVertical size={16} />
            </div>
          </div>
        )}

        <TableBlock
          id={block.id}
          data={block.metadata?.tableData}
          onTableChange={editable ? onTableChange : () => {}}
          onDelete={editable ? onDelete : () => {}}
        />
      </div>
    );
  }

  if (block.type === "document") {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="mb-1 group relative"
        onMouseEnter={() => editable && setShowHandle(true)}
        onMouseLeave={() => setShowHandle(false)}
        onTouchStart={editable ? handleLongPressStart : undefined}
        onTouchEnd={editable ? handleLongPressEnd : undefined}
      >
        {/* Handle Area - Controls visibility of + and drag handles (only when editable) */}
        {editable && (
          <div className="flex items-start gap-1 absolute left-0 top-0 -ml-20">
            {/* Remove Block Handle */}
            <button
              onClick={handleRemoveBlock}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
              title="Remove block"
            >
              <Trash size={16} />
            </button>
            {/* Add Block Handle */}
            <button
              onClick={handleAddBlock}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
              title="Add new block below"
            >
              <Plus size={16} />
            </button>

            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className={`flex-shrink-0 mt-1 p-1 rounded cursor-grab active:cursor-grabbing transition-opacity duration-200 ${
                (!isMobile && showHandle) || (isMobile && longPressActive)
                  ? "opacity-100"
                  : "opacity-0"
              } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
            >
              <GripVertical size={16} />
            </div>
          </div>
        )}

        <DocumentBlock
          id={block.id}
          content={block.content}
          isActive={isActive}
          onContentChange={editable ? onContentChange : () => {}}
          onAddBlock={editable ? onAddBlock : undefined}
          onDocumentContentUpdate={editable ? onDocumentContentUpdate : undefined}
          onDocumentSave={editable ? onDocumentSave : undefined}
          onDocumentBlockDelete={editable ? onRemoveBlock : undefined}
          onDocumentBlockCancel={editable ? onDocumentBlockCancel : undefined}
        />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-1 group relative ${getClassName()}`}
      data-style={block.metadata?.style}
      onMouseEnter={() => editable && setShowHandle(true)}
      onMouseLeave={() => setShowHandle(false)}
      onTouchStart={editable ? handleLongPressStart : undefined}
      onTouchEnd={editable ? handleLongPressEnd : undefined}
    >
      {/* Handle Area - Controls visibility of + and drag handles (only when editable) */}
      {editable && (
        <div className="flex items-start gap-1 absolute left-0 top-0 -ml-20 ">
          {/* Remove Block Handle */}
          <button
            onClick={handleRemoveBlock}
            className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
              (!isMobile && showHandle) || (isMobile && longPressActive)
                ? "opacity-100"
                : "opacity-0"
            } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
            title={t('platform.editor.sortableblock.remove_block_title_2_3')}
          >
            <Trash size={16} />
          </button>
          {/* Add Block Handle */}
          <button
            onClick={handleAddBlock}
            className={`flex-shrink-0 mt-1 p-1 rounded cursor-pointer transition-opacity duration-200 ${
              (!isMobile && showHandle) || (isMobile && longPressActive)
                ? "opacity-100"
                : "opacity-0"
            } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
            title={t('platform.editor.sortableblock.add_new_block_below_title_2_3')}
          >
            <Plus size={16} />
          </button>

          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className={`flex-shrink-0 mt-1 p-1 rounded cursor-grab active:cursor-grabbing transition-opacity duration-200 ${
              (!isMobile && showHandle) || (isMobile && longPressActive)
                ? "opacity-100"
                : "opacity-0"
            } hover:bg-editor-handle text-editor-handle hover:text-editor-handle-hover`}
          >
            <GripVertical size={16} />
          </div>
        </div>
      )}

      <TextBlock
        id={block.id}
        content={block.content}
        placeholder={getPlaceholder()}
        isActive={isActive}
        editable={editable}
        onContentChange={onContentChange}
        onEnter={onEnter}
        onBackspace={onBackspace}
        onFocus={onFocus}
        onArrowUp={onArrowUp}
        onArrowDown={onArrowDown}
        onCommand={onCommand}
        onMultilinePaste={onMultilinePaste}
        initialCursorPosition={initialCursorPosition}
        onCursorPositionApplied={onCursorPositionApplied}
        onMetadataChange={onMetadataChange}
        onMultiBlockFormatting={onMultiBlockFormatting}
        enableVariableReferences={enableVariableReferences}
        variableTypes={variableTypes}
        variableObjects={variableObjects}
      />
    </div>
  );
};
